import type { ReactElement } from 'react';
import { useTranslation } from 'react-i18next';
import {
  paiseToRupees,
  parseRupees,
  type FabricSource,
  type OrderItemInput,
  type Product,
} from '@tailoring-crm/shared';
import { Input, Label } from '@tailoring-crm/ui';
import { formatCurrency } from '../../lib/format.js';

/**
 * The cloth a garment is cut from, asked at the counter so the bill is whole before the customer
 * leaves.
 *
 * Two answers, and only one of them costs anything. A customer who brought their own material is
 * paying for the stitching — the box only records what it was, so the tailor knows which bundle.
 * Cloth from the shop's shelf is charged as metres × rate, exactly as `linePaiseOf` charges it on
 * the server, and the invoice prints it as its own "Fabric - …" line.
 *
 * Picking a fabric from the catalogue brings its name and rate; the rate stays editable, and
 * a fabric the catalogue has never heard of is simply typed. Nothing is taken off the shelf here —
 * this is what the customer pays, not a stock movement.
 */

/** As the form holds it: everything as typed, so an emptied box is not a zero. */
export interface DraftFabric {
  source: FabricSource;
  /** The catalogue product the rate came from, or `''` for one typed by hand. */
  productId: string;
  description: string;
  /** Metres for the whole line, as typed: "2.5". */
  metres: string;
  /** Rupees per metre, as typed. */
  rate: string;
}

export const EMPTY_FABRIC: DraftFabric = {
  source: 'customer',
  productId: '',
  description: '',
  metres: '',
  rate: '',
};

function paiseOf(input: string): number {
  if (input.trim() === '') return 0;
  try {
    return parseRupees(input);
  } catch {
    return 0;
  }
}

function metresOf(input: string): number {
  const metres = Number(input.trim());
  return Number.isFinite(metres) && metres > 0 ? metres : 0;
}

/** What the cloth adds to the bill — nothing unless the shop supplied it. */
export function draftFabricPaise(fabric: DraftFabric | undefined): number {
  if (!fabric || fabric.source !== 'shop') return 0;
  return Math.round(metresOf(fabric.metres) * paiseOf(fabric.rate));
}

/**
 * The block as the API takes it, or nothing at all for the customer's own cloth with no note —
 * which is what every garment booked before this field existed already sends.
 */
export function toFabricInput(fabric: DraftFabric | undefined): OrderItemInput['fabric'] {
  if (!fabric) return undefined;
  const description = fabric.description.trim() || null;
  if (fabric.source === 'customer') {
    return description ? { source: 'customer', description } : undefined;
  }
  return {
    source: 'shop',
    description,
    metres: metresOf(fabric.metres) || null,
    ratePerMetrePaise: paiseOf(fabric.rate) || null,
  };
}

export interface GarmentFabricFieldProps {
  fabric: DraftFabric;
  onChange: (fabric: DraftFabric) => void;
  /** The shop's live product list; only what is sold by the metre is offered. */
  products: readonly Product[];
  /** Prefixes every input id, so garments on one screen do not collide. */
  idPrefix: string;
}

export function GarmentFabricField({
  fabric,
  onChange,
  products,
  idPrefix,
}: GarmentFabricFieldProps): ReactElement {
  const { t } = useTranslation();
  const fabrics = products.filter((product) => product.unit === 'metre');
  const set = (patch: Partial<DraftFabric>): void => onChange({ ...fabric, ...patch });

  const chooseProduct = (productId: string): void => {
    const chosen = fabrics.find((product) => product.id === productId);
    set(
      chosen
        ? {
            productId: chosen.id,
            description: chosen.name,
            rate:
              chosen.unitPricePaise === null ? '' : String(paiseToRupees(chosen.unitPricePaise)),
          }
        : { productId: '' },
    );
  };

  return (
    <div className="flex flex-col gap-2">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <Label>{t('orders.booking.fabric.heading')}</Label>
        <div
          role="radiogroup"
          aria-label={t('orders.booking.fabric.heading')}
          className="inline-flex rounded-md border border-input p-0.5 text-sm"
        >
          {(['customer', 'shop'] as const).map((source) => (
            <button
              key={source}
              type="button"
              role="radio"
              aria-checked={fabric.source === source}
              onClick={() => set({ source })}
              className={
                fabric.source === source
                  ? 'rounded bg-primary px-3 py-1 font-medium text-primary-foreground'
                  : 'rounded px-3 py-1 text-muted-foreground hover:text-foreground'
              }
            >
              {t(`orders.booking.fabric.source.${source}`)}
            </button>
          ))}
        </div>
      </div>

      {fabric.source === 'customer' ? (
        <Input
          id={`${idPrefix}-description`}
          aria-label={t('orders.booking.fabric.description')}
          placeholder={t('orders.booking.fabric.customerPlaceholder')}
          value={fabric.description}
          onChange={(event) => set({ description: event.target.value })}
        />
      ) : (
        <div className="grid grid-cols-1 gap-3 sm:grid-cols-[2fr_1fr_1fr_auto] sm:items-end">
          <div className="flex flex-col gap-1.5">
            <Label htmlFor={`${idPrefix}-product`}>{t('orders.booking.fabric.description')}</Label>
            {fabrics.length > 0 ? (
              <select
                id={`${idPrefix}-product`}
                value={fabric.productId}
                onChange={(event) => chooseProduct(event.target.value)}
                className="h-11 rounded-md border border-input bg-background px-3 text-base sm:text-sm"
              >
                <option value="">{t('orders.booking.fabric.typeOwn')}</option>
                {fabrics.map((product) => (
                  <option key={product.id} value={product.id}>
                    {product.name}
                  </option>
                ))}
              </select>
            ) : null}
            {/* A catalogue fabric already has its name; only a typed one asks. */}
            {fabric.productId === '' ? (
              <Input
                id={fabrics.length > 0 ? `${idPrefix}-description` : `${idPrefix}-product`}
                aria-label={fabrics.length > 0 ? t('orders.booking.fabric.name') : undefined}
                value={fabric.description}
                onChange={(event) => set({ description: event.target.value })}
              />
            ) : null}
          </div>
          <div className="flex flex-col gap-1.5">
            <Label htmlFor={`${idPrefix}-metres`}>{t('orders.booking.fabric.metres')}</Label>
            <Input
              id={`${idPrefix}-metres`}
              inputMode="decimal"
              value={fabric.metres}
              onChange={(event) => set({ metres: event.target.value })}
            />
          </div>
          <div className="flex flex-col gap-1.5">
            <Label htmlFor={`${idPrefix}-rate`}>{t('orders.booking.fabric.rate')}</Label>
            <Input
              id={`${idPrefix}-rate`}
              inputMode="decimal"
              value={fabric.rate}
              onChange={(event) => set({ rate: event.target.value })}
            />
          </div>
          <span className="pb-2.5 text-right text-sm font-semibold tabular-nums">
            {formatCurrency(draftFabricPaise(fabric))}
          </span>
        </div>
      )}
    </div>
  );
}
